import { FocusArea } from 'libs/end-to-end/src/focus-area';
import React from 'react';
import { GenericCustomVizPlaceholderSvg } from 'apps/sigma2-ui/src/components/svg/GenericCustomVizPlaceholder';
import { GenericCustomVizErrorPlaceholderSvg } from 'apps/sigma2-ui/src/components/svg/GenericCustomVizErrorPlaceholder';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { GreenCheckmarkWithCircleSvg } from 'apps/sigma2-ui/src/components/svg/GreenCheckmarkWithCircle';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export interface DeepDiveNoBiasDetectedSubTabStateProps {
  comparisonGroupDisplayName: string;
  calibrationFraming: CalibrationFraming;
  metricDefinition: MyMetricDefinition;
}

export const DeepDiveNoBiasDetectedSubTabState: React.FC<
  DeepDiveNoBiasDetectedSubTabStateProps
> = ({ comparisonGroupDisplayName, metricDefinition, calibrationFraming }) => {
  const language = useLanguage();

  return (
    <Box w="full" display="flex" justifyContent="center">
      <Box maxW="prose" h="full">
        <Spacing
          direction="vertical"
          size={'sm'}
          className="items-center justify-center py-8 px-8 pt-36 bg-white h-full text-center"
        >
          <GreenCheckmarkWithCircleSvg />

          <S2Typography
            variant="headline-6-alt"
            translatedContent={language.dataIntelligenceNoBiasDetectedTitle({
              calibrationFraming: calibrationFraming,
            })}
          />
          <S2Typography
            variant="body-7"
            translatedContent={language.dataIntelligenceNoBiasDetectedBody({
              comparisonGroupDisplayName,
              metricDefinition,
              calibrationFraming,
            })}
          />
        </Spacing>
      </Box>
    </Box>
  );
};

export interface DeepDiveEmptyExplanatoryFactorsSubTabStateProps {
  referenceLevelDefinition: LevelDefinition;
  treatmentLevelDefinition: LevelDefinition;
  metricDefinition: MyMetricDefinition;
}

export const DeepDiveEmptyExplanatoryFactorsSubTabState: React.FC<
  DeepDiveEmptyExplanatoryFactorsSubTabStateProps
> = ({
  referenceLevelDefinition,
  treatmentLevelDefinition,
  metricDefinition,
}) => {
  const language = useLanguage();

  return (
    <Box w="full" display="flex" justifyContent="center">
      <Box maxW="prose" h="full">
        <Spacing
          direction="vertical"
          size={'sm'}
          className="items-center justify-center py-8 px-8 bg-white h-full text-center"
        >
          <GenericCustomVizPlaceholderSvg />

          <S2Typography
            variant="headline-6-alt"
            translatedContent={language.dataIntelligenceEmptyExplanatoryFactorsTitle()}
          />

          <S2Typography
            variant="body-7"
            translatedContent={language.dataIntelligenceEmptyExplanatoryFactorsBody(
              {
                metricDefinition,
                treatmentLevelDefinition,
                referenceLevelDefinition,
              },
            )}
          />
        </Spacing>
      </Box>
    </Box>
  );
};

export const DeepDiveFailureState: React.FC = () => {
  const language = useLanguage();

  return (
    <Card paddingClassName="p-32 pb-32">
      <Spacing
        direction="vertical"
        size={'sm'}
        className="items-center justify-center py-8 px-8 bg-white h-full text-center"
      >
        <GenericCustomVizErrorPlaceholderSvg />
        <S2Typography
          variant="headline-6-alt"
          translatedContent={language.analysisGenericFailureTitle()}
        />
        <S2Typography
          variant="body-7"
          translatedContent={language.analysisGenericFailureBody()}
        />
      </Spacing>
    </Card>
  );
};

export const DeepDiveEmptyDataState: React.FC<{
  onResetFiltersClick?: () => void;
}> = ({ onResetFiltersClick }) => {
  const language = useLanguage();

  return (
    <Spacing
      direction="vertical"
      size={'lg'}
      className="py-32 px-8 items-center justify-center w-full bg-gray-100"
    >
      <Box maxW="prose">
        <Spacing
          direction="vertical"
          size={'sm'}
          className="items-center justify-center"
        >
          <S2Typography
            variant="headline-6-alt"
            translatedContent={language.dataIntelligenceEmptyDataTitle()}
          />

          <S2Typography
            variant="body-7"
            translatedContent={language.dataIntelligenceEmptyDataBody()}
            textColorVariant="gray-600"
          />
        </Spacing>
      </Box>

      {onResetFiltersClick && (
        <Button
          behavior={{ type: 'onClick', onClick: onResetFiltersClick }}
          size="small"
        >
          <S2Typography
            variant="headline-6-alt"
            translatedContent={language.dataIntelligenceClearFiltersButton()}
            textColorVariant="violet-100"
          />
        </Button>
      )}
    </Spacing>
  );
};
